import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import * as d3 from "d3";
import { getMilestoneData } from "../../milestone/index"
import { useRequest } from "ahooks"
import { useParams } from "react-router-dom";
import { getDashboardCosDataAPI, getDashboardCostTotalAPI, getDashboardCosMajorsAPI, getProjectOwnerDashboardAPI, getProjectWorktimeCurveDataAPI } from "@/services"
import { parseTime, add, oneDecimals, formatAmount, toHsl } from "@/utils"
import type { MilestoneRender } from "@/routes/projects/detail/milestone"
import { Select, Tooltip, Spin, Switch } from "antd"
import clsx from "clsx";
import { k_milestoneStatus } from '@/routes/projects/detail/milestone/edit'

interface RenderCostItem extends API.Project.DashboardCostItem{
    monthDate: number,
    acVal: number,
    pcVal: number,
    eacVal: number,
    eacnVal: number
    isAfter: boolean
    isMs?: boolean,
    isHide?: boolean,
    milestoneCode?: string
}

const config = {
    height: 500,
    marginTop: 80,
    marginRight: 20,
    marginBottom: 50,
    marginLeft: 60
}

const getDashboardCosData = async (projectID: string, params: string[]) => {
    let list = await getDashboardCosDataAPI(projectID, params)
    if(!list || list.length <= 0) return []
    const cur = new Date()
    cur.setHours(0, 0, 0, 0)
    const curTime = cur.getTime()
    const newlist = list.map(v => {
        const item = new Date(v.month)
        // item.setDate(1)
        item.setHours(0, 0, 0, 0)
        // let acVal = add(v.oAC, v.pAC)
        // let pcVal = add(v.oPC, v.pPC)
        // let eacVal = add(v.oEAC, v.pEAC)
        // let eacnVal = add(v.oEACN, v.pEACN)
        let isAfter = false
        if (item.getTime() > curTime) {
            // acVal = add(v.oEAC, v.pEAC)
            isAfter = true
        }
        return { ...v, monthDate: item.getTime(), isAfter }
    })
    newlist.sort((a, b) => a.monthDate - b.monthDate)
    const lastItem = newlist[newlist.length - 1]
    if (Math.abs(lastItem.monthDate - curTime) < 31 * 24 * 60 * 60 * 1000) {
        lastItem.month = parseTime(cur, "{y}-{m}-{d}")
        lastItem.monthDate = curTime
    }
    return newlist
}
interface IBudgetProps{
    data: RenderCostItem[],
    milestones?: MilestoneRender[],
    totals?: API.Project.DashboardCostTotal,
    completeDate?: string,
    hasPlot?: boolean,
    filterJoin?: boolean, //部门不全选时，计划成本需要筛选进入时间
    cashFlow?: number, // 现金流
    costType?: string, // 成本筛选类型
    avoidOverlap?: boolean, // 里程碑标签是否防重叠（截图放到PPT时开启）
    onRangeChange?: (range: number[]) => void, // 时间范围变化回调（同步成员工时图）
}
const getMilestoneColor = (d: MilestoneRender) => {
    if(d.statusNum === k_milestoneStatus.finished) return "#047857"
    return d.outDate ? "#e93b40" : "#a8a8a8"
}
const XSlider: React.FC<ISvgProps & {y: number, range: number[], onChange: (val: number[])=>void}> =({ xScale, chart, y, range, onChange }) => {
    const gRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
        if (!gRef.current || !chart || !xScale) return
        const svg = d3.select(gRef.current)
        svg.selectAll("*").remove()

        let minX = chart.minDate
        let maxX = chart.maxDate
        if (range.length > 0) {
            minX = Math.min(...range)
            maxX = Math.max(...range)
        }

        // 圆点初始位置
        const points = [{ index: 0, date: minX }, { index: 1, date: maxX }];

        // 连接线
        const line = svg.append("line")
            .attr("class", "connector")
            .attr("stroke", "steelblue")
            .attr("stroke-width", 4);

        svg.selectAll(".dot")
            .data(points)
            .enter()
            .append("circle")
            .attr("class", "dot")
            .attr("r", 10)
            .attr("cx", d => xScale(d.date))
            .attr("cy", y)
            .attr("fill", "steelblue")
            .call(
                d3.drag<SVGCircleElement, { date: number, index: number }>()
                    .on("drag", function (event: d3.D3DragEvent<SVGCircleElement, { date: number, index: number }, unknown>, d){
                        const date = xScale.invert(event.x)
                        const x = xScale(date)
                        if(x < config.marginLeft || x > chart.maxX) return
                        
                        d3.select(this)
                            .attr("cx", x);
                        
                        let x0, x1
                        if (d.index === 0) {
                            x0 = date.getTime()
                            x1 = points[1].date
                        } else {
                            x1 = date.getTime()
                            x0 = points[0].date
                        }
                        
                        onChange([x0, x1])
                        updateConnector()
                    })
            );

        function updateConnector() {
            if(!xScale) return
            line.attr("x1", xScale(points[0].date))
                .attr("y1", y)
                .attr("x2", xScale(points[1].date))
                .attr("y2", y);
        }
        updateConnector()

    }, [xScale, chart, range, y])
    return <g ref={gRef}  />;
};
const XAxis: React.FC<{ scale?: d3.ScaleTime<number, number>, transform: string, chart?: Chart }> = ({ scale, transform, chart }) => {
    const axisRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
        if (axisRef.current && scale) {
            
            const xAxis = d3.axisBottom(scale)
                // .ticks(d3.timeMonth.every(num))
                .tickFormat((d) => d3.timeFormat("%Y-%m")(d as Date)); // 格式化刻度为 YYYY-MM
            
            const svg = d3.select(axisRef.current)
            svg.selectAll("*").remove()
            svg.call(xAxis);
        }
    }, [scale, chart]);

    return <g ref={axisRef} transform={transform} />;
};

const YAxis: React.FC<{ scale?: d3.ScaleLinear<number, number>, transform: string, width: number, maxVal?: number }> = ({ scale, transform, width, maxVal = 0 }) => {
    const axisRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
        if (axisRef.current && scale) {
            const yAxisTicks = scale.ticks()
                .filter(Number.isInteger);
            const useWanUnit = maxVal > 10000;
            const yAxis = d3.axisLeft(scale)
                .tickValues(yAxisTicks)
                .tickFormat((d) => {
                    const val = +d;
                    if (useWanUnit) {
                        const wan = val / 10000;
                        return Number.isInteger(wan) ? wan + '万' : wan.toFixed(1) + '万';
                    }
                    return d3.format('d')(d);
                });
            const svg = d3.select(axisRef.current)
            svg.selectAll("*").remove()
            svg.call(yAxis).call(g => g.select(".domain").remove())
                .call(g => g.selectAll(".tick line").clone()
                    .attr("class", "tick-line-dashed")
                    .attr("stroke-opacity", 0.2)
                    .attr("stroke-dasharray", "3, 3")
                    .attr("x2", width)
                )
        }
    }, [scale, maxVal]);

    return <g ref={axisRef} transform={transform} />;
};
interface Chart{
    minDate: number
    maxDate: number
    minVal: number
    maxVal: number
    maxX: number // 像素值，x轴宽度
    maxY: number // 像素值，Y轴高度
}
interface ISvgProps{
    chart?: Chart,
    xScale?: d3.ScaleTime<number, number>,
    yScale?: d3.ScaleLinear<number, number>
}
const TodayFlag: React.FC<ISvgProps> = ({chart, xScale, yScale}) => {
    const gRef = useRef<SVGSVGElement>(null);
    useEffect(() => {
        if (!gRef.current || !chart || !xScale || !yScale) return
        // 今天
        const cur = new Date()
        cur.setHours(0, 0, 0, 0)
        const curDateStr = parseTime(cur, `{y}-{m}-{d}`)
        // const todayStr = parseTime(cur, `{y}-{m}-{d}`)
        const today = d3.select(gRef.current)
        today.selectAll("*").remove()
        if (cur.getTime() >= chart.minDate && cur.getTime() <= chart.maxDate) {
            const todayX = xScale(cur)
            let x = todayX - 50
            if (todayX + 50 > chart.maxX) {
                x = chart.maxX - 100
            }
            // const today = svg.append("g").attr("class", "today")
            today.append("line")
                .attr("x1", todayX)          // 起点 x 坐标
                .attr("y1", 40)         // 起点 y 坐标
                .attr("x2", todayX)         // 终点 x 坐标
                .attr("y2", chart.maxY)         // 终点 y 坐标
                .attr("stroke", "#6592c5") // 线条颜色
                .attr("stroke-width", 2); // 线条宽度

            // 绘制蓝色背景的矩形框
            today.append("rect")
                .attr("x", x)          // 矩形左上角 x 坐标
                .attr("y", 2)          // 矩形左上角 y 坐标
                .attr("width", 100)  // 矩形宽度
                .attr("height", 40) // 矩形高度
                .attr("fill", "#d6e6f5")     // 填充颜色
                .attr("stroke", "#6592c5")   // 边框颜色
                .attr("stroke-width", 2);  // 边框宽度

            // 在矩形框中间添加文字
            today.append("text")
                .attr("x", x + 50)  // 文字水平居中
                .attr("y", 15) // 文字垂直居中
                .text("今天")                // 文字内容
                .attr("font-size", 12)             // 字体大小
                .attr("font-weight", 600)
                .attr("fill", "#3c7ac1")             // 字体颜色
                .attr("text-anchor", "middle")     // 水平对齐方式
                .attr("dominant-baseline", "middle"); // 垂直对齐方式

            today.append("text")
                .attr("x", x + 50)  // 文字水平居中
                .attr("y", 30) // 文字垂直居中
                .text(curDateStr)                // 文字内容
                .attr("font-size", 12)             // 字体大小
                .attr("font-weight", 600)
                .attr("fill", "#3c7ac1")             // 字体颜色
                .attr("text-anchor", "middle")     // 水平对齐方式
                .attr("dominant-baseline", "middle"); // 垂直对齐方式
            
            today.on("mouseover", function () {
                d3.select(gRef.current).raise()
            })
        }
    }, [chart, xScale, yScale])
    return <g ref={gRef} />;
}
const CompleteFlag: React.FC<ISvgProps & { completeDate?: string }> = ({ chart, xScale, yScale, completeDate }) => {
    const gRef = useRef<SVGSVGElement>(null);
    useEffect(() => {
        if (!gRef.current || !chart || !xScale || !yScale || !completeDate) return
        const endDate = new Date(completeDate)
        // const endDateMonth = parseTime(endDate, `{y}-{m}`)
        const endDateStr = parseTime(endDate, `{y}-{m}-{d}`)
        const endDateX = xScale(endDate)
        const svg = d3.select(gRef.current)
        svg.selectAll("*").remove()
        if (endDate.getTime() >= chart.minDate && endDate.getTime() <= chart.maxDate) {
            let x = endDateX - 50
            if (endDateX + 50 > chart.maxX) {
                x = chart.maxX - 100
            }

            svg.append("line")
                .attr("x1", endDateX)          // 起点 x 坐标
                .attr("y1", 40)         // 起点 y 坐标
                .attr("x2", endDateX)         // 终点 x 坐标
                .attr("y2", chart.maxY)         // 终点 y 坐标
                .attr("stroke", "#161616") // 线条颜色
                .attr("stroke-width", 2); // 线条宽度


            // 绘制蓝色背景的矩形框
            svg.append("rect")
                .attr("x", x)          // 矩形左上角 x 坐标
                .attr("y", 2)          // 矩形左上角 y 坐标
                .attr("width", 100)  // 矩形宽度
                .attr("height", 40) // 矩形高度
                .attr("fill", "#eeeeee")     // 填充颜色
                .attr("stroke", "#333333")   // 边框颜色
                .attr("stroke-width", 2);  // 边框宽度

            // 在矩形框中间添加文字
            svg.append("text")
                .attr("x", x + 50)  // 文字水平居中
                .attr("y", 15) // 文字垂直居中
                .text("计划完成日期")                // 文字内容
                .attr("font-size", 12)             // 字体大小
                .attr("font-weight", 600)
                .attr("fill", "#333333")             // 字体颜色
                .attr("text-anchor", "middle")     // 水平对齐方式
                .attr("dominant-baseline", "middle"); // 垂直对齐方式

            svg.append("text")
                .attr("x", x + 50)  // 文字水平居中
                .attr("y", 30) // 文字垂直居中
                .text(endDateStr)                // 文字内容
                .attr("font-size", 12)             // 字体大小
                .attr("font-weight", 600)
                .attr("fill", "#333333")             // 字体颜色
                .attr("text-anchor", "middle")     // 水平对齐方式
                .attr("dominant-baseline", "middle"); // 垂直对齐方式

            svg.on("mouseover", function () {
                d3.select(gRef.current).raise()
            })
        }
        
    }, [chart, xScale, yScale, completeDate])
    return <g ref={gRef} />;
}
// 预算成本线
const BCLine: React.FC<ISvgProps & { y?: number, textBelow?: boolean }> = ({ chart, xScale, yScale, y, textBelow }) => {
    const gRef = useRef<SVGSVGElement>(null);
    useEffect(() => {
        if (!gRef.current || !chart || !xScale || !yScale) return
        const svg = d3.select(gRef.current)
        svg.selectAll("*").remove()
        const bcVal = y || 0
        if (bcVal <= 0) return

        const lineY = yScale(bcVal)
        svg.append("line")
            .attr("x1", config.marginLeft)
            .attr("y1", lineY)
            .attr("x2", chart.maxX)
            .attr("y2", lineY)
            .attr("stroke", "purple")
            .attr("stroke-width", 2);

        svg.append("text")
            .attr("x", config.marginLeft)
            .attr("y", textBelow ? lineY + 20 : lineY - 10)
            .text(`预算成本（BC）${formatAmount(bcVal)}元`)
            .attr("font-size", 12)
            .attr("font-weight", 600)
            .attr("fill", "purple");

    }, [chart, xScale, yScale, y, textBelow])
    return <g ref={gRef} />;
}
// 现金流线
const CashFlowLine: React.FC<ISvgProps & { y?: number, textBelow?: boolean }> = ({ chart, xScale, yScale, y, textBelow }) => {
    const gRef = useRef<SVGSVGElement>(null);
    useEffect(() => {
        if (!gRef.current || !chart || !xScale || !yScale) return
        const svg = d3.select(gRef.current)
        svg.selectAll("*").remove()
        const cashVal = y || 0
        if (cashVal <= 0) return

        const lineY = yScale(cashVal)
        svg.append("line")
            .attr("x1", config.marginLeft)
            .attr("y1", lineY)
            .attr("x2", chart.maxX)
            .attr("y2", lineY)
            .attr("stroke", "#fe9f36")
            .attr("stroke-width", 2)
            .attr("stroke-dasharray", "8, 4")

        svg.append("text")
            .attr("x", config.marginLeft)
            .attr("y", textBelow ? lineY + 20 : lineY - 10)
            .text(`现金流 ${formatAmount(cashVal)}元`)
            .attr("font-size", 12)
            .attr("font-weight", 600)
            .attr("fill", "#fe9f36")

    }, [chart, xScale, yScale, y, textBelow])
    return <g ref={gRef} />;
}
// 两条绿色虚线
const EACLine: React.FC<ISvgProps & { totals?: API.Project.DashboardCostTotal, costData: RenderCostItem[], completeDate?: string, isN?: boolean }> =
    ({ chart, xScale, yScale, totals, completeDate, costData, isN }) => {
        const gRef = useRef<SVGSVGElement>(null);
        useEffect(() => {
            if (!gRef.current || !chart || !xScale || !yScale || !completeDate) return
            if (costData.length <= 0) return

            const svg = d3.select(gRef.current)
            svg.selectAll("*").remove()

            const lastItem = costData[costData.length - 1]

            const generateline = d3.line<RenderCostItem>()
                .x((d) => xScale(new Date(d.month)))
                .y((d) => yScale(d.acVal));

            // 最后的计划成本 = 预算成本线
            const val = isN ? lastItem.eacnVal : lastItem.eacVal
            const eacItem = { ...lastItem, month: completeDate, acVal: val }

            const textSvg = d3.select("#chart-text")

            const completeX = xScale(new Date(completeDate))

            svg.append("path")
                .attr("class", "data-line")
                .attr("fill", "none")
                .attr("stroke", "#75985e")
                .attr("stroke-width", 2)
                .attr("stroke-dasharray", "6,3")
                .attr("d", generateline([lastItem, eacItem]));

            svg.append("circle")
                .attr("class", "complete-circle")
                .attr("cx", completeX) // 将数据点的x坐标映射到SVG的x坐标
                .attr("cy", yScale(eacItem.acVal)) // 将数据点的y坐标映射到SVG的y坐标
                .attr("r", 3) // 圆的半径
                .attr("stroke-width", 2)
                .attr("fill", "#ffffff")
                .attr("stroke", "#75985e") // 圆的填充颜色
                .on("mouseover", function (event, d) {
                    // 鼠标悬停时添加边框
                    d3.select(this)
                        .attr("r", "5") // 添加黑色边框

                    let x = completeX - 90
                    if (completeX + 90 > chart.maxX) {
                        x  = chart.maxX - 180
                    }
                    // 在菱形上方显示文字
                    textSvg.append("foreignObject")
                        .attr("class", "diamond-text")
                        .attr("x", x)
                        .attr("y", yScale(eacItem.acVal) - 50) // 文字显示在菱形上方
                        .attr("text-anchor", "middle")
                        .attr("fill", "#75985e")
                        .attr("width", 180) // 设置宽度以限制换行
                        .attr("height", 40)
                        .append("xhtml:div")
                        .attr("class", "text-container")
                        .attr("style", "color:#75985e;font-size:12px;font-weight:600;text-align:center;background-color: #ffffff;t")
                        .html(`${isN ? "完工估算(EACN)典型" : "完工估算(EAC)非典型"}: <br> ${formatAmount(eacItem.acVal)}元`);
                })
                .on("mouseout", function () {
                    window.setTimeout(() => {
                        d3.select(this)
                            .attr("r", 3)

                        // 移除文字
                        textSvg.selectAll(".diamond-text").remove();
                    }, 10)

                });
        }, [chart, xScale, yScale, costData, totals, completeDate])
        return <g ref={gRef} />;
    }
// 计划成本-连接完成线
const PCBackLine: React.FC<ISvgProps & { totals?: API.Project.DashboardCostTotal, costData: RenderCostItem[], completeDate?: string }> =
    ({ chart, xScale, yScale, totals, completeDate, costData }) => {
    const gRef = useRef<SVGSVGElement>(null);
    useEffect(() => {
        if (!gRef.current || !chart || !xScale || !yScale || !totals || !completeDate) return
        const svg = d3.select(gRef.current)
        svg.selectAll("*").remove()
        if (costData.length <= 0) return
        if (totals.bcTotal <= 0) return

        

        const lastItem = costData[costData.length - 1]

        // 最后的计划成本 = 预算成本线
        const val = totals.bcTotal

        const textSvg = d3.select("#chart-text")
        
        const pcItem = { ...lastItem, month: completeDate, pcVal: val}
        const pcline = d3.line<RenderCostItem>()
            .x((d)=>xScale(new Date(d.month)))
            .y((d) =>yScale(d.pcVal));
        svg.append("path")
            .attr("class", "data-line")
            .attr("fill", "none")
            .attr("stroke", "#3278c1")
            .attr("stroke-width", 2)
            .attr("d", pcline([lastItem, pcItem]));

        svg.append("circle")
            .attr("class", "complete-circle")
            .attr("cx", xScale(new Date(completeDate))) // 将数据点的x坐标映射到SVG的x坐标
            .attr("cy", yScale(val)) // 将数据点的y坐标映射到SVG的y坐标
            .attr("r", 3) // 圆的半径
            .attr("stroke-width", 2)
            .attr("fill", "#ffffff")
            .attr("stroke", "#3278c1") // 圆的填充颜色
            .on("mouseover", function (event, d) {
                // 鼠标悬停时添加边框
                d3.select(this)
                    .attr("r", "5") // 添加黑色边框

                // 在上方显示文字
                textSvg.append("foreignObject")
                    .attr("class", "diamond-text")
                    .attr("x", xScale(new Date(completeDate)) - 90)
                    .attr("y", yScale(val) - 40) // 文字显示在菱形上方
                    .attr("width", 180) // 设置宽度以限制换行
                    .attr("height", 40)
                    .append("xhtml:div")
                    .attr("class", "text-container")
                    .attr("style", "color:#3278c1;font-size:12px;font-weight:600;text-align:center;background-color: #ffffff;")
                    .html(`计划成本(PC):\n ${pcItem.pcVal}元`);
            })
            .on("mouseout", function () {
                window.setTimeout(() => {
                    d3.select(this)
                        .attr("r", 3)

                    // 移除文字
                    textSvg.selectAll(".diamond-text").remove();
                }, 10)

            });

    }, [chart, xScale, yScale, costData, totals, completeDate])
    return <g ref={gRef} />;
}
const k_lineType = {
    AC: "ac",
    PC: "pc",
    AC_MONTH: "acMonth" // 当月人力成本（AC 的月度差值）
}
let timer: number | null = null;
// 裁剪开头的 0：从第一个大于 0 的值开始，前面的 0 全部去掉（中间和后面的 0 不动）
function trimLeadingZeros(arr: RenderCostItem[], getVal: (d: RenderCostItem) => number): RenderCostItem[] {
    const firstPos = arr.findIndex(v => getVal(v) > 0)
    if (firstPos === -1) return []
    return arr.slice(firstPos)
}
const SolidLine: React.FC<ISvgProps & { costData: RenderCostItem[], type: string, msPCs?: RenderCostItem[], filterJoin?: boolean }> =
    ({ chart, xScale, yScale, costData, type, msPCs, filterJoin }) => {
    const gRef = useRef<SVGSVGElement>(null);
    useEffect(() => {
        if (!gRef.current || !chart || !xScale || !yScale) return
        const svg = d3.select(gRef.current)
        svg.selectAll("*").remove()
        if (costData.length <= 0) return

        let list = [...costData]
        if (type === k_lineType.PC && msPCs && msPCs.length > 0) {
            const msItems = msPCs.filter(v=>!v.isHide)
            // list = [...list, ...msItems]
            list.sort((a, b) => a.monthDate - b.monthDate)
            let maxVal = 0
            list = list.map(v => {
                maxVal = Math.max(maxVal, v.pcVal)
                return {
                    ...v,
                    pcVal: maxVal
                }
            })
            if (filterJoin) {
                const firstItem = costData[0]
                list = list.filter(v => v.monthDate >= firstItem.monthDate)
            }
        }
        if (type === k_lineType.PC) {
            let newList: RenderCostItem[] = []
            let mapitems = new Map<number, RenderCostItem>()
            list.forEach(v => {
                const item = mapitems.get(v.monthDate)
                if (item) {
                    if (v.pcVal >= item.pcVal) {
                        mapitems.set(v.monthDate, v)
                    }
                } else {
                    mapitems.set(v.monthDate, v)
                }
            })
            list = Array.from(mapitems.values())
            list.sort((a, b) => a.monthDate - b.monthDate)

        }
        // 当月人力成本：计算月度增量
        if (type === k_lineType.AC_MONTH) {
            list.sort((a, b) => a.monthDate - b.monthDate)
            list = list.map((v, i) => {
                if (i === 0) return { ...v } // 首月保持原值
                const prev = list[i - 1]
                let deltaAcVal: number
                if (prev.acVal === 0) {
                    deltaAcVal = v.acVal // 上月为0，保持原值
                } else if (prev.acVal === v.acVal) {
                    deltaAcVal = 0 // 无增量
                } else {
                    deltaAcVal = v.acVal - prev.acVal
                }
                return { ...v, acVal: deltaAcVal }
            })
        }
        // 三条线都裁剪开头的 0：从第一个大于 0 的值开始（中间和后面的 0 不动）
        list = trimLeadingZeros(list, v => type === k_lineType.PC ? v.pcVal : v.acVal)
        
        
        const textSvg = d3.select("#chart-text")

        const getTypeVal = (d: RenderCostItem) => {
            if (type === k_lineType.AC) return d.acVal
            if (type === k_lineType.PC) return d.pcVal
            if (type === k_lineType.AC_MONTH) return d.acVal
            return 0
        }
        const getTypeColor = () => {
            if (type === k_lineType.AC) return "#fe9f36"
            if (type === k_lineType.PC) return "#3278c1"
            if (type === k_lineType.AC_MONTH) return "#75985e"
            return ""
        }
        const getTypeLabel = (d: RenderCostItem) => {
            if (type === k_lineType.AC) return `${d.month}<br>实际成本（AC）`
            if (type === k_lineType.PC) return `${d.month}<br>计划成本（PC）`
            if (type === k_lineType.AC_MONTH) return `${d.month}<br>当月人力成本`
            return ""
        }
        const color = getTypeColor()

        const pcline = d3.line<RenderCostItem>()
            .x((d) => xScale(d.monthDate))
            .y((d) => yScale(getTypeVal(d)))
            .curve(d3.curveMonotoneX); 
        
        
        const textWidth = type === k_lineType.AC ? 180 : type === k_lineType.AC_MONTH ? 200 : 220
        
        svg.selectAll("*").remove()
        svg.append("path").lower()
            .datum(list)
            .attr("fill", "none")
            .attr("stroke", color)
            .attr("stroke-width", 2)
            .attr("d", pcline(list)) // 使用路径生成器绘制路径

        svg.selectAll("circle.data-circle")
            .data(list || [])
            .join("circle")
            .attr("class", "data-circle")
            .attr("cx", (d) => xScale(d.monthDate)) // 将数据点的x坐标映射到SVG的x坐标
            .attr("cy", (d) => yScale(getTypeVal(d))) // 将数据点的y坐标映射到SVG的y坐标
            .attr("r", 3) // 圆的半径
            .attr("stroke-width", 2)
            .attr("fill", "#ffffff")
            .attr("stroke", color) // 圆的填充颜色
            .on("mouseover", function (event, d) {

                let x = xScale(d.monthDate)
                if (x + (textWidth / 2) > chart.maxX) {
                    x = chart.maxX - textWidth
                } else {
                    x = x - (textWidth / 2)
                }
                let textHeight = 40 
                if (d.isMs) {
                    textHeight = 60
                }
                d3.select(gRef.current).raise();
                // 鼠标悬停时添加边框
                d3.select(this)
                    .attr("r", "5") // 添加黑色边框
                
                textSvg.append("foreignObject")
                    .attr("class", "diamond-text")
                    .attr("x", x)
                    .attr("y", yScale(getTypeVal(d)) - textHeight - 10) // 文字显示在上方
                    .attr("text-anchor", "middle")
                    .attr("width", textWidth) // 设置宽度以限制换行
                    .attr("height", textHeight)
                    .append("xhtml:div")
                    .attr("class", "text-container")
                    .attr("style", `color:${color};font-size:12px;font-weight:600;text-align:center;background-color: #ffffff;`)
                    .html(`${getTypeLabel(d)}: ${formatAmount(getTypeVal(d))}元`);

                // 在上方显示文字
                // textSvg.append("text")
                //     .attr("class", "diamond-text")
                //     .attr("x", xScale(new Date(d.month)))
                //     .attr("y", yScale(getTypeVal(d)) - 20) // 文字显示在上方
                //     .attr("text-anchor", "middle")
                //     .attr("font-size", 12)             // 字体大小
                //     .attr("font-weight", 600)
                //     .attr("fill", color)
                //     .text(`计划成本(PC): ${getTypeVal(d)}元`);
            })
            .on("mouseout", function () {
                
                d3.select(this)
                    .attr("r", 3)
                // 移除文字
                textSvg.selectAll(".diamond-text").remove();

            });

    }, [chart, xScale, yScale, costData, msPCs])
    return <g ref={gRef} />;
}
// 里程碑标签布局结果
interface MilestoneLabelLayout {
    milestone: MilestoneRender
    x: number
    color: string
    dateStr: string
    name: string
    precent: string
    labelWidth: number
    labelX: number
    labelY: number
    centerX: number // 标签框水平中心（贴近边界被夹取后会与里程碑 x 不一致）
}

const k_milestoneLabelHeight = 42
// 最底下一行标签与图表顶部的间距
const k_milestoneLabelClearance = 38

/**
 * 计算里程碑标签的防重叠布局：
 * 1. 过滤出图表可视范围内的里程碑，并计算每个标签的宽度与左边界；
 * 2. 按左边界排序后贪心分配层级：0=基准行，1=往上放，>=2 往下放第二、第三层；
 * 3. 归一化垂直偏移（最上面的层贴顶），返回每个标签的 labelY 与总行数 laneCount。
 */
function computeMilestoneLabels(
    milestones: MilestoneRender[],
    chart: Chart,
    xScale: d3.ScaleTime<number, number>,
    avoidOverlap = true
): { labels: MilestoneLabelLayout[], laneCount: number } {
    const labelHeight = k_milestoneLabelHeight

    const items: (MilestoneLabelLayout & { right: number, level: number })[] = []
    milestones.forEach((d) => {
        const date = d.actualDate || d.milestoneDate
        if (!date) return

        const msDate = new Date(date)
        msDate.setHours(0, 0, 0, 0)
        const msTime = msDate.getTime()

        // 超出图表可视范围的不画
        if (msTime < chart.minDate || msTime > chart.maxDate) return

        const x = xScale(msDate)
        const color = getMilestoneColor(d)
        const dateStr = parseTime(msDate, '{y}-{m}-{d}')

        // 标签文本：取三行中最宽的一行来估算宽度
        const name = d.milestoneName.length > 8 ? d.milestoneName.substring(0, 8) + '…' : d.milestoneName
        const nameWidth = name.length * 11   // 中文，11px 字体
        const dateWidth = dateStr.length * 7 // 数字/符号，10px 字体
        const percentWidth = d.precent.length * 7
        const labelWidth = Math.min(Math.max(Math.max(nameWidth, dateWidth, percentWidth) + 16, 60), 170)
        let labelX = x - labelWidth / 2
        if (labelX < 0) labelX = 2
        if (labelX + labelWidth > chart.maxX) labelX = chart.maxX - labelWidth - 2

        items.push({
            milestone: d,
            x,
            color,
            dateStr,
            name,
            precent: d.precent,
            labelWidth,
            labelX,
            labelY: 0,
            centerX: labelX + labelWidth / 2,
            right: labelX + labelWidth,
            level: 0,
        })
    })

    if (items.length === 0) return { labels: [], laneCount: 0 }

    // 关闭防重叠时：所有标签放同一行，允许互相遮挡
    if (!avoidOverlap) {
        return { labels: items, laneCount: 1 }
    }

    // 按左边界排序，保证同一层内只需与「最后放置标签的右边界」比较即可判断是否重叠
    items.sort((a, b) => a.labelX - b.labelX)

    // 每层记录已放置标签的最大右边界，贪心找到第一个能放下的层
    const laneRights: number[] = []
    items.forEach((it) => {
        let level = laneRights.findIndex(right => it.labelX >= right)
        if (level === -1) {
            level = laneRights.length
            laneRights.push(it.right)
        } else {
            laneRights[level] = it.right
        }
        it.level = level
    })

    // level -> 垂直偏移（单位：labelHeight）：0=基准，1=往上，>=2 往下第 (level-1) 层
    const offsetOf = (level: number) => (level === 0 ? 0 : level === 1 ? -1 : level - 1)
    let minOffset = 0
    let maxOffset = 0
    items.forEach(it => {
        const off = offsetOf(it.level)
        minOffset = Math.min(minOffset, off)
        maxOffset = Math.max(maxOffset, off)
    })

    // 归一化：最上面的层贴顶向下依次排开，避免负坐标被 SVG 裁剪
    items.forEach(it => {
        it.labelY = (offsetOf(it.level) - minOffset) * labelHeight
    })

    const laneCount = maxOffset - minOffset + 1
    return { labels: items, laneCount }
}

// 里程碑竖虚线标记（类似 TodayFlag，在图表中显示竖虚线和标签）
const MilestoneLine: React.FC<ISvgProps & { labels: MilestoneLabelLayout[] }> =
    ({ chart, xScale, yScale, labels }) => {
    const gRef = useRef<SVGSVGElement>(null);
    useEffect(() => {
        if (!gRef.current || !chart || !xScale || !yScale) return
        if (labels.length <= 0) return

        const svg = d3.select(gRef.current)
        svg.selectAll("*").remove()

        const labelHeight = k_milestoneLabelHeight

        labels.forEach((it) => {
            const { centerX, color, dateStr, name, precent, labelWidth, labelX, labelY } = it

            // 每个里程碑独立分组，hover 时上浮（同时提升整个 MilestoneLine 容器，防止被 Today/Complete 盖住）
            const group = svg.append("g")
                .attr("class", "milestone-group")
                .attr("cursor", "pointer")
                .on("mouseenter", function () {
                    d3.select(this).raise()
                    // 将根容器也提升，确保盖在 TodayFlag / CompleteFlag 之上
                    const rootGroup = d3.select(gRef.current)
                    if (!rootGroup.empty()) rootGroup.raise()
                })

            // 竖虚线（从标签框底部连到图表底部，与标签框中心对齐）
            group.append("line")
                .attr("class", "milestone-dash-line")
                .attr("x1", centerX)
                .attr("y1", labelY + labelHeight)
                .attr("x2", centerX)
                .attr("y2", chart.maxY)
                .attr("stroke", color)
                .attr("stroke-width", 1.5)
                .attr("stroke-dasharray", "4, 4")
                .attr("stroke-opacity", 0.6)

            // 白色背景矩形
            group.append("rect")
                .attr("x", labelX)
                .attr("y", labelY)
                .attr("width", labelWidth)
                .attr("height", labelHeight)
                .attr("fill", "#ffffff")
                .attr("stroke", color)
                .attr("stroke-width", 1)
                .attr("rx", 4)
                .attr("stroke-opacity", 0.8)

            // 里程碑名称
            group.append("text")
                .attr("x", centerX)
                .attr("y", labelY + 12)
                .text(name)
                .attr("font-size", 11)
                .attr("font-weight", 600)
                .attr("fill", color)
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "middle")

            // 日期
            group.append("text")
                .attr("x", centerX)
                .attr("y", labelY + 24)
                .text(dateStr)
                .attr("font-size", 10)
                .attr("fill", color)
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "middle")

            // 比例
            group.append("text")
                .attr("x", centerX)
                .attr("y", labelY + 36)
                .text(precent)
                .attr("font-size", 10)
                .attr("fill", color)
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "middle")
        })
    }, [chart, xScale, yScale, labels])
    return <g ref={gRef} />;
}
const k_noteType = {
    CV: "cv",
    ETC: "etc",
    VAC: "vac"
}
const NoteLine: React.FC<ISvgProps & { costData: RenderCostItem[], todayItem?: RenderCostItem,  type: string, completeDate?: string, totals?: API.Project.DashboardCostTotal }> =
    ({ chart, xScale, yScale, costData, type, completeDate, totals, todayItem }) => {
    const gRef = useRef<SVGSVGElement>(null);
    useEffect(() => {
        if (!gRef.current || !chart || !xScale || !yScale) return
        const svg = d3.select(gRef.current)
        svg.selectAll("*").remove()
        if (costData.length <= 0) return

        const getTypeLabel = () => {
            if (type === k_noteType.CV) return "成本偏差（CV）"
            if (type === k_noteType.ETC) return "完工尚需估算（ETC）非典型"
            if (type === k_noteType.VAC) return "完工偏差（VACN）典型"
            return ""
        }
        const getTypeColor = () => {
            if (type === k_noteType.CV) return "#9ba9cb"
            return "#75985e"
        }
        
        let width = 100
        if (type === k_noteType.ETC || type === k_noteType.VAC) {
            width = 160
        }
        const color = getTypeColor()
        const label = getTypeLabel()


        // 本月
        const cur = new Date()
        cur.setHours(0, 0, 0, 0)
        const curDateStr = parseTime(cur, `{y}-{m}`)
        
        

        let x1 = 0, x2 = 0, y1 = 0, y2 = 0
        if (type === k_noteType.CV) {
            const item = todayItem
            if (!item) {
                svg.selectAll("*").remove()
                return
            }
            x1 = xScale(new Date(curDateStr))
            y1 = yScale(item.acVal)
            y2 = yScale(item.pcVal)

            if (x1 < chart.maxX / 2) {
                x2 = x1 + 100
            } else {
                x2 = x1 - 100
            }
        } else if (type === k_noteType.ETC) {
            const item = todayItem
            if (!item || !completeDate || !totals) {
                svg.selectAll("*").remove()
                return
            }
            x1 = xScale(new Date(curDateStr))
            y1 = yScale(totals.bcTotal)
            x2 = xScale(new Date(completeDate))
            y2 = yScale(item.pcVal)
        } else if (type === k_noteType.VAC) {
            const item = costData[costData.length - 1]
            if (!completeDate || !totals) {
                svg.selectAll("*").remove()
                return
            }
            x1 = xScale(new Date(completeDate))
            y1 = yScale(item.eacnVal)
            y2 = yScale(totals.bcTotal)
            if (x1 < chart.maxX / 2) {
                x2 = x1 + 100
            } else {
                x2 = x1 - 100
            }
        }
        
        
        
        svg.selectAll("*").remove()
        // 绘制第一根水平线
        svg.append("line")
            .attr("x1", x1)
            .attr("y1", y1)
            .attr("x2", x2)
            .attr("y2", y1)
            .attr("stroke-opacity", 0.2)
            .attr("stroke", "#000")
            .attr("stroke-width", 2);

        // 绘制第二根水平线
        svg.append("line")
            .attr("x1", x1)
            .attr("y1", y2)
            .attr("x2", x2)
            .attr("y2", y2)
            .attr("stroke-opacity", 0.2)
            .attr("stroke", "#000")
            .attr("stroke-width", 2);

        // 计算中间位置
        const midX = (x1 + x2) / 2; // 中间点的 X 坐标
        const midY = (y1 + y2) / 2; // 中间点的 Y 坐标

        // 绘制双向箭头
        const arrowPath = `M ${midX} ${y1} L ${midX} ${y2}`; // 垂直线
        const arrowHead = "M 0 0 L 10 -5 L 10 5 Z"; // 箭头头部

        svg.append("path")
            .attr("d", arrowPath)
            .attr("stroke-opacity", 0.2)
            .attr("stroke", "#000")
            .attr("stroke-width", 2)
            .attr("fill", "none");

        let rotate = 90
        if (y1 < y2) {
            rotate = -90
        }
        
        // 添加上箭头
        svg.append("path")
            .attr("d", arrowHead)
            .attr("transform", `translate(${midX}, ${y1}) rotate(${-rotate})`) // 旋转并定位rotate(90)
            .attr("fill", "#a1a1a1");

        // 添加下箭头
        svg.append("path")
            .attr("d", arrowHead)
            .attr("transform", `translate(${midX}, ${y2}) rotate(${rotate})`) // 定位rotate(-90)
            .attr("fill", "#a1a1a1");
        
        svg.append("foreignObject")
            .attr("class", "diamond-text")
            .attr("x", midX - (width / 2))
            .attr("y", midY - 10)
            .attr("text-anchor", "middle")
            .attr("width", width) // 设置宽度以限制换行
            .attr("height", 20)
            .append("xhtml:div")
            .attr("class", "text-container")
            .attr("style", `color:${color};font-size:12px;font-weight:600;text-align:center;background-color: #ffffff;`)
            .html(label);

        // 添加解释文字
        // svg.append("text")
        //     .attr("x", midX)
        //     .attr("y", midY)
        //     .attr("text-anchor", "middle") // 文字居中
        //     .attr("dy", "0.35em") // 微调垂直位置
        //     .attr("font-size", 12)
        //     .attr("font-weight", 600)
        //     .attr("fill", color)
        //     .text(label);

    }, [chart, xScale, yScale, costData, todayItem])
    return <g ref={gRef} />;
}
const Budget: React.FC<IBudgetProps> = ({ data: costData, milestones = [], totals, completeDate, filterJoin, hasPlot, cashFlow, costType, avoidOverlap = true, onRangeChange }) => {
    
    const svgRef = useRef<SVGSVGElement>(null);
    // const xScaleRef = useRef<d3.ScaleTime<number, number, never>>()
    // const yScaleRef = useRef<d3.ScaleLinear<number, number, never>>()
    const [svgWidth, setSvgWidth] = useState(500)
    const [range, setRange] = useState<number[]>([])
    const isFreezeRange = useRef(false)
    // 图例显示/隐藏状态
    const [visibleLines, setVisibleLines] = useState<Record<string, boolean>>({
        PC: true,
        AC: true,
        AC_MONTH: true,
        BC: true,
        cashFlow: true,
        EAC: true,
        EACN: true,
    })
    const legendItems = useMemo(() => {
        const items = [
            { key: 'PC', label: '计划成本（PC）', color: '#3278c1' },
            { key: 'AC', label: '实际成本（AC）', color: '#fe9f36' },
            { key: 'AC_MONTH', label: '当月人力成本', color: '#75985e' },
            { key: 'BC', label: '预算成本（BC）', color: 'purple' },
            { key: 'cashFlow', label: '现金流', color: '#fe9f36', dashed: true },
            { key: 'EAC', label: '完工估算（EAC）', color: '#75985e', dashed: true },
            { key: 'EACN', label: '典型完工估算', color: '#75985e', dashed: true },
        ]
        // 项目成本模式下隐藏当月人力成本
        if (costType === k_costType.pr) {
            return items.filter(item => item.key !== 'AC_MONTH')
        }
        return items
    }, [costType])
    const handleLegendClick = (key: string) => {
        setVisibleLines(prev => ({ ...prev, [key]: !prev[key] }))
    }
    const handleChart = useCallback(() => {
        const {
            height,
            marginBottom,
            marginLeft
        } = config

        const maxX = svgWidth - 20
        const maxY = height - marginBottom + 20

        let endDate: Date | undefined
        if (completeDate) {
            endDate = new Date(completeDate)
        }
        let minVal = 0, maxVal = 0
        let minDate: number | undefined, maxDate: number | undefined
        costData.forEach(v => {
            minVal = Math.min(minVal, v.acVal)
            maxVal = Math.max(maxVal, v.acVal)
            if (minDate === undefined) {
                minDate = v.monthDate
            }
            if (maxDate === undefined) {
                maxDate = v.monthDate
            }
            minDate = Math.min(minDate, v.monthDate)
            maxDate = Math.max(maxDate, v.monthDate)
        })
        if (milestones.length > 0) {
            milestones.forEach(v => {
                let dateStr = v.actualDate || v.milestoneDate
                if (!dateStr) return
                const date = new Date(dateStr)
                date.setHours(0, 0, 0, 0)
                const dateTime = date.getTime()

                if (minDate === undefined) {
                    minDate = dateTime
                }
                if (maxDate === undefined) {
                    maxDate = dateTime
                }
                minDate = Math.min(minDate, dateTime)
                maxDate = Math.max(maxDate, dateTime)
            })
        }

        if (completeDate && costData.length > 0) {
            let lastItem = costData[costData.length - 1]
            const eac = lastItem.eacVal
            const neac = lastItem.eacnVal
            maxVal = Math.max(maxVal, eac)
            maxVal = Math.max(maxVal, neac)
        }
        if (totals) {
            maxVal = Math.max(maxVal, totals.bcTotal)
        }
        if (cashFlow) {
            maxVal = Math.max(maxVal, cashFlow)
        }

        if (minDate === undefined || maxDate === undefined) return
        if (endDate) {
            minDate = Math.min(minDate, endDate.getTime())
            maxDate = Math.max(maxDate, endDate.getTime())
        }
        const maxEnd = new Date(maxDate)
        maxEnd.setMonth(maxEnd.getMonth() + 1);
        maxDate = maxEnd.getTime()
        const minStart = new Date(minDate)
        minStart.setMonth(minStart.getMonth() - 1);
        minDate = minStart.getTime()

        return {
            minDate,
            maxDate,
            minVal,
            maxVal,
            maxX,
            maxY,
        }
    }, [svgWidth, costData, milestones, totals, completeDate])

    // chart 是派生状态，用 useMemo 计算，避免 useEffect + setState 导致的无限循环
    const chart = useMemo(() => {
        return handleChart() || { minDate: 0, maxDate: 0, minVal: 0, maxVal: 0, maxX: 100, maxY: 0 }
    }, [handleChart])

    // 仅在数据加载且未冻结范围时初始化 range
    useEffect(() => {
        if (!isFreezeRange.current && chart.minDate > 0 && chart.maxDate > 0) {
            const newRange = [chart.minDate, chart.maxDate]
            setRange(newRange)
            onRangeChange?.(newRange)
        }
    }, [chart.minDate, chart.maxDate, onRangeChange])

    const renderChart = useMemo(() => {
        if (range.length !== 2) return chart
        return {
            ...chart,
            minDate: Math.min(...range),
            maxDate: Math.max(...range)
        }
    }, [range, chart])

    const renderMilestonePCs = useMemo(() => {
        if (!milestones) return []
        if (!totals || totals.bcTotal <= 0) return []
        const mslist: RenderCostItem[] = []
        const item = costData[0]

        const curDate = new Date()
        curDate.setHours(0, 0, 0, 0)
        const curTime = curDate.getTime()

        const bcTotal = totals.bcTotal

        milestones.forEach(v => {
            if (!v.actualDate && !v.milestoneDate) return
            let date = v.actualDate || v.milestoneDate

            let monthDate = new Date(date)
            monthDate.setHours(0, 0, 0, 0)
            const monthTime = monthDate.getTime()
            let hide = false
            if (monthTime < curTime && v.statusNum === k_milestoneStatus.overdue) {
                hide = true
            }
            const newItem = {
                ...item,
                pcVal: v.precentNum * bcTotal / 100,
                isMs: true,
                month: `${date}<br>里程碑：${v.milestoneName} - ${v.precent}`,
                monthDate: monthDate.getTime(),
                isHide: hide,
                milestoneCode: v.milestoneCode
            }
            mslist.push(newItem)
        })
        return mslist
    }, [milestones, totals])

    const todayItem = useMemo(() => {
        const msItems = renderMilestonePCs.filter(v => !v.isHide)
        let list = [...costData, ...msItems]
        list.sort((a, b) => a.monthDate - b.monthDate)
        let maxVal = 0
        list = list.map(v => {
            maxVal = Math.max(maxVal, v.pcVal)
            return {
                ...v,
                pcVal: maxVal
            }
        })
        // 今天，使用本月的数据
        const cur = new Date()
        cur.setHours(0, 0, 0, 0)
        const curDateStr = parseTime(cur, `{y}-{m}`)
        const item = list.find(v => v.month === curDateStr)
        return item
    }, [renderMilestonePCs, costData])


    const xScale = useMemo(() => {
        if (!renderChart) return
        return d3.scaleTime() // 使用band比例尺来处理离散的字符串数据
            .domain([renderChart.minDate, renderChart.maxDate]) // 定义坐标轴的字符串值
            .range([config.marginLeft, renderChart.maxX]) // SVG中的像素范围
    }, [renderChart])

    const xAllScale = useMemo(() => {
        if (!chart) return
        return d3.scaleTime() // 使用band比例尺来处理离散的字符串数据
            .domain([chart.minDate, chart.maxDate]) // 定义坐标轴的字符串值
            .range([config.marginLeft, chart.maxX]) // SVG中的像素范围
    }, [chart])

    // 里程碑标签防重叠布局（向上/向下分层）
    const milestoneLayout = useMemo(() => {
        if (hasPlot || !renderChart || !xScale || !milestones || milestones.length <= 0) {
            return { labels: [] as MilestoneLabelLayout[], laneCount: 0 }
        }
        return computeMilestoneLabels(milestones, renderChart, xScale, avoidOverlap)
    }, [hasPlot, renderChart, xScale, milestones, avoidOverlap])

    // 顶部间距随标签叠加层数变化，保证标签框不会覆盖图表
    const marginTop = useMemo(() => {
        const need = k_milestoneLabelHeight * milestoneLayout.laneCount + k_milestoneLabelClearance
        return Math.max(config.marginTop, need)
    }, [milestoneLayout.laneCount])

    // 图表总高度：里程碑分层增加顶部空间的同时，保证绘图区高度不变
    const chartHeight = config.height + (marginTop - config.marginTop)

    // 绘图区底部（X轴位置），随总高度下移
    const plotBottom = chartHeight - config.marginBottom + 20

    const yScale = useMemo(() => {
        if (!chart) return
        return d3.scaleLinear()
            .domain([chart.minVal, chart.maxVal]) // 数据的最小值和最大值
            .range([chartHeight - config.marginBottom, marginTop]); // SVG中的像素范围;
    }, [chart, chartHeight, marginTop])

    // 竖线/旗帜类组件使用：maxY 随总高度下移，其余字段与 renderChart 一致
    const renderChartView = useMemo(() => ({ ...renderChart, maxY: plotBottom }), [renderChart, plotBottom])

    // 检测预算成本线和现金流线是否相近（< 30px），避免文字叠加
    const isCashFlowCloseToBC = useMemo(() => {
        if (!yScale || totals?.bcTotal == null || cashFlow == null) return false
        const bcY = yScale(totals.bcTotal)
        const cashY = yScale(cashFlow)
        return Math.abs(bcY - cashY) < 30
    }, [yScale, totals?.bcTotal, cashFlow])

    // 清除 #chart-text 中的残留 tooltip
    useEffect(() => {
        d3.select("#chart-text").selectAll("*").remove()
    }, [chart, xScale, costData])

    useEffect(() => {
        const resize = () => {
            if (!svgRef.current) return

            setSvgWidth(svgRef.current.parentElement?.clientWidth || 500)
        }

        resize()
        window.addEventListener("resize", resize)
        return () => {
            window.removeEventListener("resize", resize)
        }
    }, [])

    return (
        <div>
            {/* 图例 */}
            <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2 justify-center text-xs">
                {legendItems.map(item => (
                    <div key={item.key} className="flex items-center cursor-pointer select-none gap-1"
                        onClick={() => handleLegendClick(item.key)}
                        style={{ opacity: visibleLines[item.key] ? 1 : 0.35 }}>
                        {item.dashed ? (
                            <span className="inline-block w-4" style={{ borderTop: `2px dashed ${item.color}`, height: 0 }}></span>
                        ) : (
                            <span className="inline-block w-4 h-[2px]" style={{ backgroundColor: item.color }}></span>
                        )}
                        <span>{item.label}</span>
                    </div>
                ))}
            </div>
            <svg ref={svgRef} width={svgWidth} height={chartHeight + 100}>
                <defs>
                    <clipPath id="chart-area">
                        <rect x={config.marginLeft} width={chart.maxX - config.marginLeft} height={chartHeight - config.marginBottom + 20} />
                    </clipPath>
                </defs>
                <XAxis scale={xScale} transform={`translate(0,${chartHeight - config.marginBottom + 20})`} chart={renderChart} />
                <YAxis scale={yScale} transform={`translate(${config.marginLeft}, 0)`} width={chart ? (chart.maxX - config.marginLeft) : 0} maxVal={chart.maxVal} />

                <g transform={`translate(${0},${0})`}>
                    <TodayFlag xScale={xScale} yScale={yScale} chart={renderChartView}  />
                    <CompleteFlag xScale={xScale} yScale={yScale} chart={renderChartView} completeDate={completeDate} />
                    {!hasPlot && (
                        <MilestoneLine xScale={xScale} yScale={yScale} chart={renderChartView} labels={milestoneLayout.labels} />
                    )}
                </g>

                <g transform={`translate(${0},${0})`} clipPath="url(#chart-area)">
                    <NoteLine xScale={xScale} yScale={yScale} chart={renderChart} costData={costData} todayItem={todayItem} totals={totals} completeDate={completeDate} type={k_noteType.CV} />
                    <NoteLine xScale={xScale} yScale={yScale} chart={renderChart} costData={costData} todayItem={todayItem} totals={totals} completeDate={completeDate} type={k_noteType.ETC} />
                    <NoteLine xScale={xScale} yScale={yScale} chart={renderChart} costData={costData} todayItem={todayItem} totals={totals} completeDate={completeDate} type={k_noteType.VAC} />
                    
                    {visibleLines.BC && <BCLine xScale={xScale} yScale={yScale} chart={renderChart} y={totals?.bcTotal} textBelow={isCashFlowCloseToBC && !!totals && !!cashFlow && cashFlow > totals.bcTotal} />}
                    {cashFlow != null && cashFlow > 0 && visibleLines.cashFlow && (
                        <CashFlowLine xScale={xScale} yScale={yScale} chart={renderChart} y={cashFlow} textBelow={isCashFlowCloseToBC} />
                    )}
                    {visibleLines.EAC && <EACLine xScale={xScale} yScale={yScale} chart={renderChart} totals={totals} costData={costData} completeDate={completeDate} />}
                    {visibleLines.EACN && <EACLine xScale={xScale} yScale={yScale} chart={renderChart} totals={totals} costData={costData} completeDate={completeDate} isN={true} />}
                    {visibleLines.AC && <SolidLine xScale={xScale} yScale={yScale} chart={renderChart} costData={costData} msPCs={renderMilestonePCs} type={k_lineType.AC} />}
                    {visibleLines.PC && <SolidLine xScale={xScale} yScale={yScale} chart={renderChart} filterJoin={filterJoin} costData={costData} msPCs={renderMilestonePCs} type={k_lineType.PC} />}
                    {visibleLines.AC_MONTH && costType !== k_costType.pr && <SolidLine xScale={xScale} yScale={yScale} chart={renderChart} costData={costData} msPCs={renderMilestonePCs} type={k_lineType.AC_MONTH} />}
                </g>
                

                <g>
                    <XAxis scale={xAllScale} transform={`translate(0,${chartHeight + 60})`} chart={chart} />
                    <XSlider xScale={xAllScale} chart={chart} range={range} onChange={(v) => { isFreezeRange.current = true; setRange(v); onRangeChange?.(v); }} y={chartHeight + 60} />
                </g>
                <g id="chart-text"></g>
            </svg>
        </div>
    )
}
const Totals: React.FC<{data?: API.Project.DashboardCostTotal}> = ({data}) => {

    return (
        <div className="flex gap-x-10 gap-y-4 mb-4 flex-wrap justify-center font-bold text-center">
            <div>
                <div className="text-[#3278c1]">计划成本（PC）</div>
                <div>{formatAmount(data?.PC)}元</div>
            </div>
            <div>
                <div className="text-[#fe9f36]">实际成本（AC）</div>
                <div>{formatAmount(data?.AC)}元</div>
            </div>
            <div>
                <div className="text-[#9ba9cb]">成本偏差（CV）</div>
                <div className={clsx(data && data.CV < 0 && "text-red-500")}>{formatAmount(data?.CV)}元</div>
            </div>
            <div>
                <div className="text-[#9ba9cb]">成本绩效指数（CPI）</div>
                <div className={clsx(data && data.CPI < 1 && "text-red-500")}>{data?.CPI}</div>
            </div>
            <div>
                <div className="text-[#75985e]">完工尚需估算（ETC）</div>
                <div>典型：{formatAmount(data?.ETCN)}元</div>
                <div>非典型：{formatAmount(data?.ETC)}元</div>
            </div>
            <div>
                <div className="text-[#75985e]">完工估算（EAC）</div>
                <div>典型：{formatAmount(data?.EACN)}元</div>
                <div>非典型：{formatAmount(data?.EAC)}元</div>
            </div>
            <div>
                <div className="text-[#9ba9cb]">完工偏差（VAC）</div>
                <div className={clsx(data && data.VACN < 0 && "text-red-500")}>典型：{formatAmount(data?.VACN)}元</div>
                <div className={clsx(data && data.VAC < 0 && "text-red-500")}>非典型：{formatAmount(data?.VAC)}元</div>
            </div>
            <div>
                <div className="text-[#9ba9cb]">完工成本绩效指数（ECPI）</div>
                <div className={clsx(data && data.ECPI < 1 && "text-red-500")}>典型：{data?.ECPIN}</div>
                <div className={clsx(data && data.ECPIN < 1 && "text-red-500")}>非典型：{data?.ECPI}</div>
            </div>
        </div>
    )
}
// ============ 成员工时曲线图 ============
interface MemberWorktimeItem {
    name: string
    color: string
    totalHours: number // API 返回的最后一个月的累计总工时
    data: { monthDate: number; hours: number; month: string }[]
}

const worktimeChartConfig = {
    height: 400,
    marginTop: 80,
    marginRight: 20,
    marginBottom: 50,
    marginLeft: 50,
}

const MemberWorktimeChart: React.FC<{ data: MemberWorktimeItem[], milestones?: MilestoneRender[], minDate?: number, maxDate?: number, showEndLabels?: boolean, avoidOverlap?: boolean }> = ({ data, milestones = [], minDate, maxDate, showEndLabels = true, avoidOverlap = true }) => {
    const svgRef = useRef<SVGSVGElement>(null)
    const [svgWidth, setSvgWidth] = useState(500)

    const chart = useMemo(() => {
        if (data.length === 0) return null
        // 如果传入了外部范围（来自成本表时间轴），则用外部范围
        let domainMin: number
        let domainMax: number
        if (minDate != null && maxDate != null) {
            domainMin = minDate
            domainMax = maxDate
        } else {
            domainMin = Infinity
            domainMax = -Infinity
            data.forEach(user => {
                user.data.forEach(d => {
                    domainMin = Math.min(domainMin, d.monthDate)
                    domainMax = Math.max(domainMax, d.monthDate)
                })
            })
            if (!isFinite(domainMin) || !isFinite(domainMax)) return null
            const maxEnd = new Date(domainMax); maxEnd.setMonth(maxEnd.getMonth() + 1)
            const minStart = new Date(domainMin); minStart.setMonth(minStart.getMonth() - 1)
            domainMin = minStart.getTime()
            domainMax = maxEnd.getTime()
        }
        let maxVal = 0
        data.forEach(user => {
            user.data.forEach(d => {
                maxVal = Math.max(maxVal, d.hours)
            })
        })
        const maxX = svgWidth - worktimeChartConfig.marginRight
        const maxY = worktimeChartConfig.height - worktimeChartConfig.marginBottom + 20
        return { minDate: domainMin, maxDate: domainMax, minVal: 0, maxVal, maxX, maxY }
    }, [data, svgWidth, minDate, maxDate])

    const xScale = useMemo(() => {
        if (!chart) return
        return d3.scaleTime()
            .domain([chart.minDate, chart.maxDate])
            .range([worktimeChartConfig.marginLeft, chart.maxX])
    }, [chart])

    // 里程碑标签防重叠布局（向上/向下分层）
    const milestoneLayout = useMemo(() => {
        if (!chart || !xScale || !milestones || milestones.length === 0) {
            return { labels: [] as MilestoneLabelLayout[], laneCount: 0 }
        }
        return computeMilestoneLabels(milestones, chart, xScale, avoidOverlap)
    }, [chart, xScale, milestones, avoidOverlap])

    // 顶部间距随标签叠加层数变化，保证标签框不会覆盖图表
    const marginTop = useMemo(() => {
        const need = k_milestoneLabelHeight * milestoneLayout.laneCount + k_milestoneLabelClearance
        return Math.max(worktimeChartConfig.marginTop, need)
    }, [milestoneLayout.laneCount])

    // 图表总高度：里程碑分层增加顶部空间的同时，保证绘图区高度不变
    const chartHeight = worktimeChartConfig.height + (marginTop - worktimeChartConfig.marginTop)

    const yScale = useMemo(() => {
        if (!chart) return
        return d3.scaleLinear()
            .domain([chart.minVal, chart.maxVal])
            .range([chartHeight - worktimeChartConfig.marginBottom, marginTop])
    }, [chart, chartHeight, marginTop])

    useEffect(() => {
        const resize = () => {
            if (!svgRef.current) return
            setSvgWidth(svgRef.current.parentElement?.clientWidth || 500)
        }
        resize()
        window.addEventListener("resize", resize)
        return () => window.removeEventListener("resize", resize)
    }, [])

    useEffect(() => {
        if (!svgRef.current || !chart || !xScale || !yScale) return
        const svg = d3.select(svgRef.current)
        svg.selectAll("g.chart-content").remove()
        svg.selectAll("g.x-axis").remove()
        svg.selectAll("g.y-axis").remove()
        svg.selectAll("g.tooltip").remove()

        const chartG = svg.append("g").attr("class", "chart-content")

        // tooltip 容器
        const tooltip = svg.append("g").attr("class", "tooltip").style("display", "none")
        tooltip.append("rect")
            .attr("fill", "#fff")
            .attr("stroke", "#ccc")
            .attr("stroke-width", 1)
            .attr("rx", 4)
            .attr("width", 140)
            .attr("height", 40)
        const tooltipText = tooltip.append("text")
            .attr("x", 8)
            .attr("y", 16)
            .attr("font-size", 11)
            .attr("fill", "#333")

        // Y 轴
        const yAxisG = svg.append("g").attr("class", "y-axis")
            .attr("transform", `translate(${worktimeChartConfig.marginLeft}, 0)`)
        const yTicks = yScale.ticks().filter(Number.isInteger)
        const yAxis = d3.axisLeft(yScale).tickValues(yTicks).tickFormat(d3.format("d"))
        yAxisG.call(yAxis).call(g => g.select(".domain").remove())
            .call(g => g.selectAll(".tick line").clone()
                .attr("stroke-opacity", 0.2)
                .attr("stroke-dasharray", "3, 3")
                .attr("x2", chart.maxX - worktimeChartConfig.marginLeft))

        // X 轴
        const xAxisG = svg.append("g").attr("class", "x-axis")
            .attr("transform", `translate(0, ${chartHeight - worktimeChartConfig.marginBottom + 20})`)
        xAxisG.call(d3.axisBottom(xScale).tickFormat(d => d3.timeFormat("%Y-%m")(d as Date)))

        // 曲线
        const line = d3.line<{ monthDate: number; hours: number }>()
            .x(d => xScale(d.monthDate))
            .y(d => yScale(d.hours))
            .curve(d3.curveMonotoneX)

        data.forEach((user, uIdx) => {
            if (user.data.length === 0) return

            // 每个用户一个分组，hover 时整体上浮
            const userG = chartG.append("g").attr("class", `user-group-${uIdx}`)

            // 可见曲线
            userG.append("path")
                .datum(user.data)
                .attr("fill", "none")
                .attr("stroke", user.color)
                .attr("stroke-width", 2)
                .attr("d", line)

            // 透明宽线用于扩大 hover 区域
            userG.append("path")
                .datum(user.data)
                .attr("fill", "none")
                .attr("stroke", "transparent")
                .attr("stroke-width", 12)
                .attr("d", line)
                .on("mouseover", function (event) {
                    userG.raise()
                    const [mx, my] = d3.pointer(event, svg.node())
                    let tx = mx + 12, ty = my - 36
                    if (tx + 140 > chart.maxX) tx = mx - 152
                    if (ty < 0) ty = 4
                    tooltip.style("display", null)
                        .attr("transform", `translate(${tx}, ${ty})`)
                    tooltipText.selectAll("tspan").remove()
                    tooltipText.append("tspan").attr("x", 8).attr("dy", 0).text(user.name)
                    tooltipText.append("tspan").attr("x", 8).attr("dy", 16).text(`总工时 ${user.totalHours}h`)
                })
                .on("mouseout", function () {
                    tooltip.style("display", "none")
                })

            // 线尾标签：用户名 + 总工时（取可视范围内最后一个点，防止超出图表）
            if (showEndLabels) {
                const visibleData = user.data.filter(d => d.monthDate >= chart.minDate && d.monthDate <= chart.maxDate)
                if (visibleData.length > 0) {
                    const lastVisible = visibleData.reduce((a, b) => a.monthDate > b.monthDate ? a : b)
                    const labelText = `${user.name} ${user.totalHours}h`
                    const labelWidth = user.name.length * 11 + String(user.totalHours).length * 7 + 20
                    let endLabelX = xScale(lastVisible.monthDate) + 6
                    if (endLabelX + labelWidth > chart.maxX) {
                        endLabelX = xScale(lastVisible.monthDate) - 6
                    }
                    const endLabelY = yScale(lastVisible.hours)
                    userG.append("text")
                        .attr("x", endLabelX)
                        .attr("y", endLabelY)
                        .text(labelText)
                        .attr("font-size", 11)
                        .attr("fill", user.color)
                        .attr("dominant-baseline", "middle")
                        .style("cursor", "pointer")
                        .on("mouseover", function () {
                            userG.raise()
                        })
                }
            }

            // 数据点
            userG.selectAll("circle")
                .data(user.data)
                .join("circle")
                .attr("cx", d => xScale(d.monthDate))
                .attr("cy", d => yScale(d.hours))
                .attr("r", 2.5)
                .attr("fill", user.color)
                .attr("stroke", "#fff")
                .attr("stroke-width", 1)
                .on("mouseover", function (event, d) {
                    userG.raise()
                    d3.select(this).transition().duration(150).attr("r", 5)
                    const [mx, my] = d3.pointer(event, svg.node())
                    let tx = mx + 12, ty = my - 50
                    if (tx + 140 > chart.maxX) tx = mx - 152
                    if (ty < 0) ty = 4
                    tooltip.style("display", null)
                        .attr("transform", `translate(${tx}, ${ty})`)
                    tooltipText.selectAll("tspan").remove()
                    tooltipText.append("tspan").attr("x", 8).attr("dy", 0).text(`${d.month}`)
                    tooltipText.append("tspan").attr("x", 8).attr("dy", 16).text(`${user.name}  累计 ${d.hours}h`)
                })
                .on("mouseout", function () {
                    d3.select(this).transition().duration(150).attr("r", 2.5)
                    tooltip.style("display", "none")
                })
        })

        // 里程碑竖虚线（和成本图表一致，标签防重叠分层）
        const labelHeight = k_milestoneLabelHeight
        milestoneLayout.labels.forEach((it) => {
            const { centerX, color, dateStr, name, precent, labelWidth, labelX, labelY } = it

            // 每个里程碑独立分组，hover 时上浮到最上层，盖住曲线
            const msGroup = chartG.append("g")
                .attr("class", "worktime-milestone-group")
                .attr("cursor", "pointer")
                .on("mouseenter", function () {
                    d3.select(this).raise()
                })

            // 竖虚线（从标签框底部连到图表底部，与标签框中心对齐）
            msGroup.append("line")
                .attr("class", "worktime-milestone-dash")
                .attr("x1", centerX)
                .attr("y1", labelY + labelHeight)
                .attr("x2", centerX)
                .attr("y2", chartHeight - worktimeChartConfig.marginBottom + 20)
                .attr("stroke", color)
                .attr("stroke-width", 1.5)
                .attr("stroke-dasharray", "4, 4")
                .attr("stroke-opacity", 0.6)

            msGroup.append("rect")
                .attr("x", labelX)
                .attr("y", labelY)
                .attr("width", labelWidth)
                .attr("height", labelHeight)
                .attr("fill", "#ffffff")
                .attr("stroke", color)
                .attr("stroke-width", 1)
                .attr("rx", 4)
                .attr("stroke-opacity", 0.8)

            msGroup.append("text")
                .attr("x", centerX)
                .attr("y", labelY + 12)
                .text(name)
                .attr("font-size", 11)
                .attr("font-weight", 600)
                .attr("fill", color)
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "middle")

            msGroup.append("text")
                .attr("x", centerX)
                .attr("y", labelY + 24)
                .text(dateStr)
                .attr("font-size", 10)
                .attr("fill", color)
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "middle")

            msGroup.append("text")
                .attr("x", centerX)
                .attr("y", labelY + 36)
                .text(precent)
                .attr("font-size", 10)
                .attr("fill", color)
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "middle")
        })

    }, [chart, xScale, yScale, data, milestoneLayout, chartHeight, showEndLabels])

    if (!chart) return null

    return (
        <svg ref={svgRef} width={svgWidth} height={chartHeight + 80}>
        </svg>
    )
}

const k_dateType = {
    month: 0,
    season: 1,
    year: 2
}
type MajorOption = API.Project.DashboardCostMajor & {
    label: string,
    value: string,
}
const k_costType = {
    all: "",
    hr: "hr", // 人力->pAC
    pr: "pr" // 项目->oAC
}
const Index = () => {
    const [dateType, setDateType] = useState(k_dateType.month)
    const [costType, setCostType] = useState(k_costType.all)
    const [hasPlot, setHasPlot] = useState(false)
    // 成本表时间轴范围，用于同步成员工时图
    const [zoomedRange, setZoomedRange] = useState<number[]>([])
    // 稳定化回调，避免每次渲染生成新函数导致 Budget 内部 effect 无限循环
    const handleRangeChange = useCallback((v: number[]) => {
        setZoomedRange(v)
    }, [])
    // 成员工时图：隐藏的成员名
    const [hiddenMemberNames, setHiddenMemberNames] = useState<Set<string>>(new Set())
    // 成员工时图：线尾标签开关
    const [showEndLabels, setShowEndLabels] = useState(true)
    // 里程碑标签防重叠开关（截图放到PPT时开启，平时放一行更好看）
    const [avoidOverlap, setAvoidOverlap] = useState(false)

    const { projectID = "" } = useParams();
    const { data: milestones } = useRequest(getMilestoneData, {
        defaultParams: [projectID || 0],
        onSuccess: (res) => {
            if (res && res.length > 0 && res[0].plot) {
                setHasPlot(true)
            }
        }
    })
    const completeDate = useMemo(() => {
        if (!milestones || milestones.length <= 0) return ""
        const item = milestones[milestones.length - 1]
        return item.actualDate || item.milestoneDate
    }, [milestones])
    const { data: costTotal, run: fetchCostTotal, loading: loadingCostTotal } = useRequest(getDashboardCostTotalAPI, {
        defaultParams: [projectID, [], ""],
        manual: true
    })
    const renderCostTotal = useMemo(() => {
        if (!costTotal) return undefined
        const total = {
            ...costTotal
        }
        total.bcTotal = total.BC
        return total
    }, [costTotal])
    
    const { data: costData = [], run: fetchCosts, loading: loadingCosts } = useRequest(getDashboardCosData, {
        defaultParams: [projectID, []],
        manual: true
    })
    const renderCostData = useMemo(() => {
        if (!costData) {
            return []
        }

        let list = costData.map(v => {
            const item = new Date(v.month)
            item.setHours(0, 0, 0, 0)

            let acVal = 0
            let pcVal = 0
            let eacVal = 0
            let eacnVal = 0

            if (costType === k_costType.all) {
                acVal = add(v.oAC, v.pAC)
                pcVal = add(v.oPC, v.pPC)
                eacVal = add(v.oEAC, v.pEAC)
                eacnVal = add(v.oEACN, v.pEACN)
            } else if (costType === k_costType.hr) {
                acVal = v.pAC
                pcVal = v.pPC
                eacVal = v.pEAC
                eacnVal = v.pEACN
            } else if (costType === k_costType.pr) {
                acVal = v.oAC
                pcVal = v.oPC
                eacVal = v.oEAC
                eacnVal = v.oEACN
            }

            return { ...v, acVal, pcVal, eacVal, eacnVal }
        })
        
        if (milestones && milestones.length > 0) {
            let firstMsItem = milestones[0]
            let msDate = firstMsItem.actualDate || firstMsItem.milestoneDate
            if (firstMsItem.milestoneCode === "start_time" && msDate) {
                const msDateTime = new Date(msDate).getTime()
                let prev: RenderCostItem | undefined = undefined
                let hasStart = false
                let newList: RenderCostItem[] = []

                for (const item of list) {

                    if (item.monthDate >= msDateTime || item.acVal > 0 || item.pcVal > 0) {
                        hasStart = true
                        newList.push(item)
                    }
                    if (!hasStart) {
                        prev = item
                    }
                }
                if (prev && prev.acVal === 0) {
                    newList = [{ ...prev }, ...newList]
                }
                list = newList
            }
        }

        return list
    }, [costData, costType, milestones])
    const { data: majors = [], loading: loadingMajors } = useRequest(async (projectID) => {
        let res = await getDashboardCosMajorsAPI(projectID)
        return res.map(v => ({ ...v, label: `${v.professionComName}-${v.professionDeptName}-${v.professionName}`, value: `${v.professioncomcode},${v.professiondeptcode},${v.profession}` }))
    }, {
        defaultParams: [projectID],
        onSuccess: (data) => {
            if(!data || data.length <= 0) return
            setSelectedDeparts(data)
        }
    })
    // const [selectedDepart, setSelectedDepart] = useState<string>("")
    const [selectedDeparts, setSelectedDeparts] = useState<MajorOption[]>([])
    const handleChange = (value: string) => {
        // setSelectedDepart(value)
        const item = majors.find(v => v.value === value)
        if(!item) return
    };
    const onDepartChange = (val: string[], option: any) => {
        setSelectedDeparts(option as MajorOption[])
    }
    
    // 使用 ref 存储最新的请求函数，避免将其加入 effect 依赖导致循环
    const fetchCostTotalRef = useRef(fetchCostTotal)
    fetchCostTotalRef.current = fetchCostTotal
    const fetchCostsRef = useRef(fetchCosts)
    fetchCostsRef.current = fetchCosts

    const { data: worktimeCurveData = [], run: fetchWorktimeCurveData, loading: loadingWorktimeCurveData } = useRequest(getProjectWorktimeCurveDataAPI, {
        defaultParams: [projectID, []],
        manual: true
    })

    // 策划产值（用于计算现金流）
    const { data: projectOverview, run: fetchProjectOverview, loading: loadingProjectOverview } = useRequest(getProjectOwnerDashboardAPI, {
        manual: true
    })
    const fetchProjectOverviewRef = useRef(fetchProjectOverview)
    fetchProjectOverviewRef.current = fetchProjectOverview

    useEffect(() => {
        const departs = selectedDeparts.map(v => v.value)
        if(departs.length <= 0) return
        fetchCostTotalRef.current(projectID, departs, costType)
        fetchCostsRef.current(projectID, departs)
        fetchWorktimeCurveData(projectID, departs)

        // 现金流只在全部成本/人力成本时获取
        if (costType !== k_costType.pr) {
            fetchProjectOverviewRef.current(projectID, departs)
        }
    }, [selectedDeparts, projectID, costType])

    // 计算现金流：全部成本=策划产值*40%，人力成本=策划产值*32%
    const cashFlow = useMemo(() => {
        if (!projectOverview || costType === k_costType.pr) return undefined
        const planOutputValue = Number(projectOverview.planOutputValue) || 0
        if (planOutputValue <= 0) return undefined
        if (costType === k_costType.hr) {
            return planOutputValue * 0.32
        }
        // 全部成本
        return planOutputValue * 0.4
    }, [projectOverview, costType])

    // 处理成员工时曲线数据：直接使用 API 返回的累计总工时
    const worktimeChartData = useMemo(() => {
        if (!worktimeCurveData || worktimeCurveData.length === 0) return []
        const colorMap = new Map<string, string>()
        return worktimeCurveData.map(user => {
            if (!colorMap.has(user.name)) {
                colorMap.set(user.name, toHsl(user.name))
            }
            const sorted = [...user.data].sort((a, b) => a.month.localeCompare(b.month))
            // 前置裁剪 0：从第一个大于 0 的工时开始，前面的 0 全部去掉
            const firstPos = sorted.findIndex(item => item.hours > 0)
            const list = firstPos === -1 ? [] : sorted.slice(firstPos)
            // 从后往前处理：如果和前一个月相减为0，则从末尾去除；中间相减为0的不处理
            for (let i = list.length - 1; i > 0; i--) {
                if (list[i].hours - list[i - 1].hours === 0) {
                    list.pop()
                } else {
                    break
                }
            }
            const monthlyData: { monthDate: number; hours: number; month: string }[] = list.map(item => ({
                monthDate: new Date(item.month + "-01").getTime(),
                hours: item.hours,
                month: item.month,
            }))
            return { name: user.name, color: colorMap.get(user.name)!, totalHours: list.length > 0 ? list[list.length - 1].hours : 0, data: monthlyData }
        })
    }, [worktimeCurveData])

    
    const onCostTypeChange = (val: string) => {
        setCostType(val)
        // const departs = selectedDeparts.map(v => v.value)
        // fetchCostTotal(projectID, departs, val)
    }
    return (
        <div>
            <div className="flex items-center mb-4">
                <span className="mr-2">部门专业筛选: </span>
                <Select
                    mode='multiple'
                    className="w-[460px]"
                    placeholder='按部门专业筛选'
                    options={majors}
                    value={selectedDeparts.map(v=>v.value)}
                    onChange={onDepartChange}
                    maxTagCount='responsive'
                    maxTagPlaceholder={(omittedValues) => (
                        <Tooltip
                            styles={{ root: { pointerEvents: 'none' } }}
                            title={omittedValues.map(({ label }) => label).join(', ')}
                        >
                            <span>+ {omittedValues.length} ...</span>
                        </Tooltip>
                    )}
                />
                <span className="ml-6 mr-2">成本筛选: </span>
                <Select
                    value={costType}
                    style={{ width: 120 }}
                    onChange={onCostTypeChange}
                    options={[
                        { value: k_costType.all, label: '全部成本' },
                        { value: k_costType.hr, label: '人力成本' },
                        { value: k_costType.pr, label: '项目成本' },
                    ]}
                />
                <span className="ml-6 mr-2">里程碑标签防重叠: </span>
                <Switch checked={avoidOverlap} onChange={setAvoidOverlap} />
            </div>
            <Spin spinning={loadingCosts || loadingWorktimeCurveData || loadingCostTotal}>
                <Totals data={renderCostTotal} />
                <Budget data={renderCostData} hasPlot={hasPlot} milestones={milestones} totals={renderCostTotal} completeDate={completeDate} filterJoin={true} cashFlow={cashFlow} costType={costType} avoidOverlap={avoidOverlap} onRangeChange={handleRangeChange} />
                {worktimeChartData.length > 0 && (
                    <div className="mt-8">
                        <div className="font-bold text-sm mb-4">成员工时</div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2 items-center">
                            {worktimeChartData.map(user => {
                                const hidden = hiddenMemberNames.has(user.name)
                                return (
                                    <div key={user.name} className="flex items-center cursor-pointer select-none text-xs gap-1"
                                        onClick={() => {
                                            const next = new Set(hiddenMemberNames)
                                            if (hidden) next.delete(user.name)
                                            else next.add(user.name)
                                            setHiddenMemberNames(next)
                                        }}
                                        style={{ opacity: hidden ? 0.35 : 1 }}>
                                        <span className="inline-block w-4 h-[2px]" style={{ backgroundColor: user.color }}></span>
                                        <span>{user.name}</span>
                                        <span className="text-gray-500">{user.totalHours}h</span>
                                    </div>
                                )
                            })}
                            <div className="flex items-center cursor-pointer select-none text-xs gap-1 ml-2 pl-2 border-l border-gray-300"
                                onClick={() => setShowEndLabels(v => !v)}
                                style={{ opacity: showEndLabels ? 1 : 0.35 }}>
                                <span className={clsx("inline-block w-3.5 h-3.5 border rounded", showEndLabels ? "bg-blue-500 border-blue-500" : "bg-white border-gray-400")}>
                                    {showEndLabels && <span className="block text-white text-[10px] leading-[12px] text-center">✓</span>}
                                </span>
                                <span>线尾标签</span>
                            </div>
                        </div>
                        <MemberWorktimeChart data={worktimeChartData.filter(u => !hiddenMemberNames.has(u.name))} milestones={milestones} minDate={zoomedRange.length === 2 ? zoomedRange[0] : undefined} maxDate={zoomedRange.length === 2 ? zoomedRange[1] : undefined} showEndLabels={showEndLabels} avoidOverlap={avoidOverlap} />
                    </div>
                )}
            </Spin>
            {/* <Milestone data={milestones} className="mt-0 pt-0" /> */}
        </div>
    )
}
export default Index